/**
 * Générateur — Il décompose un problème en sous-problèmes et crée un
 * « bloc personnalisé ».
 * Attendu ID : 250 | Niveau : 3ème | Domaine : Algorithmique (d21, O1 niv. 3)
 *
 * Source doc prof (D2S2.Ex.7-8, B2S1.Ex.10, Créé n°18). Plan auto-validé
 * 2026-07-18 : « losange » (le bloc « Losange » : angles EXTÉRIEURS —
 * tourner ↻ de α puis 180−α, le lutin avançant avant de tourner ; fix
 * 2026-07-28, d'après brevet), « carre » (le bloc paramétré
 * « Carré (n) » + script principal des trois carrés — le paramètre, marche
 * décisive du niveau 3), « boules » (le bloc « Tirer deux boules » : que
 * valent A, B, C), « init » (où placer l'initialisation du compteur, QCM).
 */

import { randomChoice, randInt, makeQcm, makeTexteTrou } from '../../../utils';
import { imgElement, svgScript } from '../../4eme/algorithmique/algo';

const ATTENDU_ID = 250;
const L_LOSANGE = 'Le bloc « Losange » (angles extérieurs)';
const L_CARRE = 'Le bloc paramétré « Carré (n) »';
const L_BOULES = 'Le bloc « Tirer deux boules »';
const L_INIT = 'Où initialiser le compteur ?';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « losange » ═════════

const genLosangeTrous = () => {
    const alpha = randomChoice([30, 40, 45, 60, 70]);
    const c = randInt(3, 8) * 10;
    // Ordre des trous ÉCHANGÉ (fix rapport 3e, 2026-07-28) : le lutin AVANCE
    // avant de tourner, sa première rotation a donc lieu au sommet ATTEINT —
    // celui de (180 − α)° — où l'angle extérieur vaut α ; la seconde a lieu au
    // sommet de α°, où il vaut 180 − α. L'ancienne clé (180 − α puis α)
    // validait l'élève qui ignorait « en partant d'un sommet d'angle α » et
    // comptait faux celui qui suivait la consigne. La figure se refermant dans
    // les deux ordres, seul le sommet de départ départage.
    const phrase = `répéter 2 fois [avancer de ${c} ; tourner ↻ de {{${alpha}}} degrés ; avancer de ${c} ; tourner ↻ de {{${180 - alpha}}} degrés].`;
    const payload = makeTexteTrou(phrase,
        `<p>D'après brevet. Le bloc personnalisé « Losange » doit tracer un losange d'angles ${alpha}° et `
        + `${180 - alpha}°, de côté ${c} pas, en partant d'un sommet d'angle ${alpha}°.</p>`
        // Le lutin tourne des angles EXTÉRIEURS : c'est la difficulté même du
        // tracé par script, donner la formule 180 − x rendait la carte triviale.
        + '<p><b>Complète les deux angles du bloc.</b></p>',
        { ...opts(L_LOSANGE), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svgScript([
        { t: 'hat', txt: 'définir Losange' },
        { t: 'repeat', n: 2, body: [
            { t: 'move', txt: `avancer de ${c}` },
            { t: 'move', txt: 'tourner ↻ de … degrés' },
            { t: 'move', txt: `avancer de ${c}` },
            { t: 'move', txt: 'tourner ↻ de … degrés' },
        ] },
    ]));
    payload._meta.params = { alpha, c };
    return payload;
};

// ═════════ « carre » — le bloc paramétré ═════════

const genCarreTrous = () => {
    const c1 = randInt(6, 16) * 5;
    const pas = randomChoice([10, 15, 20]);
    const c2 = c1 + pas;
    const c3 = c2 + pas;
    const phrase = `Bloc : répéter {{4}} fois [avancer de n ; tourner ↻ de {{90}} degrés]. `
        + `Script : Carré (${c1}) ; avancer de ${c1} ; Carré ({{${c2}}}) ; avancer de ${c2} ; Carré ({{${c3}}}).`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut tracer trois carrés de côtés ${c1}, ${c2} et ${c3}, posés côte à côte sur une même `
        + 'ligne, du plus petit au plus grand. On crée un bloc personnalisé « Carré » avec un '
        + 'PARAMÈTRE n : le même bloc trace un carré de n\'importe quelle taille.</p>'
        + '<p><b>Complète le bloc puis le script principal.</b></p>',
        { ...opts(L_CARRE), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svgScript([
        { t: 'hat', txt: 'définir Carré (n)' },
        { t: 'repeat', n: '… fois', body: [
            { t: 'move', txt: 'avancer de n' },
            { t: 'move', txt: 'tourner ↻ de … degrés' },
        ] },
    ]));
    payload._meta.params = { c1, c2, c3 };
    return payload;
};

// ═════════ « boules » — que valent A, B, C ═════════

const genBoulesTrous = () => {
    let nbSim; let maxB; let maxR;
    for (let essai = 0; essai < 20; essai++) {
        nbSim = randomChoice([500, 1000, 2000, 5000]);
        maxB = randInt(4, 6);
        maxR = randInt(4, 7);
        if (maxB !== maxR) break;
    }
    // Effectifs des urnes : boules numérotées de 2 à maxB/maxR « sans saut »
    // → l'élève retrouve B = nB + 1 et C = nR + 1 (bornes du bloc aléatoire).
    // (La réécriture de l'énoncé référençait `nbSeries`/`parSerie`/`nB`/`nR`
    // jamais définis : S-CRASH à 100 % des tirages. Les « séries » sont
    // abandonnées — le script ne comporte qu'une seule boucle « répéter A
    // fois », l'énoncé doit dire la même chose.)
    const nB = maxB - 1;
    const nR = maxR - 1;
    const phrase = `A = {{${nbSim}}} ; B = {{${maxB}}} ; C = {{${maxR}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>D'après brevet. On simule ${nbSim} fois l'expérience. `
        + `L'urne bleue contient ${nB} boules, numérotées à partir de 2 sans saut (2, 3, …) ; `
        + `l'urne rouge en contient ${nR}, numérotées de la même façon. On tire une boule bleue puis une `
        + 'boule rouge.</p>'
        + '<p><code>définir Tirer deux boules : mettre Boule bleue à (nombre aléatoire entre 2 et B) ; '
        + 'mettre Boule rouge à (nombre aléatoire entre 2 et C)</code></p>'
        + '<p><code>quand le drapeau est cliqué ; répéter A fois [Tirer deux boules ; '
        + 'si Boule bleue = Boule rouge alors ajouter 1 à Doubles]</code></p>'
        // « Le bloc encapsule le sous-problème un tirage » expliquait le rôle du
        // bloc personnalisé, que la carte fait justement identifier.
        + '<p><b>Que valent A, B et C ?</b></p>',
        { ...opts(L_BOULES), mode: 'saisie' });
    payload._meta.params = { nbSim, maxB, maxR };
    return payload;
};

// ═════════ « init » — QCM ═════════

const CHOIX_INIT = [
    'AVANT la boucle : le compteur doit partir de 0 une seule fois',
    'Dans la boucle, avant le SI : il serait remis à 0 à chaque tour',
    'Dans le SI : il serait remis à 0 à chaque succès',
];

const genInitQcm = () => {
    const compteur = randomChoice(['Doubles', 'Six obtenus', 'Réussites', 'Points']);
    const nbSim = randomChoice([200, 500, 1000, 2000, 10000]);
    const [action, cond] = randomChoice([
        ['lancer le dé', 'le dé affiche 6'],
        ['tirer une carte', 'la carte est un as'],
        ['lancer les deux dés', 'les deux dés sont égaux'],
        ['faire tourner la roue', 'la roue s\'arrête sur le rouge'],
    ]);
    const payload = makeQcm([...CHOIX_INIT], CHOIX_INIT[0],
        `<p>Dans un script de simulation, la variable « ${compteur} » compte les succès : `
        + `répéter ${nbSim} fois [${action} ; si ${cond} alors ajouter 1 à ${compteur}].</p>`
        + `<p><b>Où faut-il placer le bloc « mettre ${compteur} à 0 » ?</b></p>`,
        opts(L_INIT));
    payload._meta.params = { compteur, nbSim, action };
    return payload;
};

const GENERATORS = {
    losange_texte_trou_saisie: genLosangeTrous,
    carre_texte_trou_saisie: genCarreTrous,
    boules_texte_trou_saisie: genBoulesTrous,
    init_qcm_unique: genInitQcm,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'losange', label: L_LOSANGE, formatsList: buildFormats('losange') },
    { id: 'carre', label: L_CARRE, formatsList: buildFormats('carre') },
    { id: 'boules', label: L_BOULES, formatsList: buildFormats('boules') },
    { id: 'init', label: L_INIT, formatsList: buildFormats('init') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
